import { Component, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { ActionStatus, UserAction } from '@/application/types';
import { showErrorResponse } from '@/application/snackbar/service';
import { languageMap, mediumMap } from '@/helpers/data';
import { Medium } from '@/domain/types';
import MasterDataTab from '../master-data-tab/master-data-tab.vue';
import DataDistributionTab from '../data-distribution-tab/data-distribution-tab.vue';
import ProductOrdersTab from '../product-orders-tab/product-orders-tab.vue';
import RelatedCalendarsTab from '../related-calendars-tab/related-calendars-tab.vue';
import RelatedSPPsTab from '../related-ssbs-tab/related-ssbs-tab.vue';
import { ProductDataDistribution, ProductIdentification, ProductRawImport } from '../../types';

const productSearchState = namespace('productSearch');

const defaultTab = 'tab-master-data';

@Component({
  components: {
    MasterDataTab,
    DataDistributionTab,
    ProductOrdersTab,
    RelatedCalendarsTab,
    RelatedSPPsTab,
  },
})
export default class ProductDetails extends Vue {

  @productSearchState.State
  readonly getProductIdentificationsByEANStatus!: ActionStatus;

  @productSearchState.State
  readonly getProductIdentificationStatus!: ActionStatus;

  @productSearchState.State
  readonly getProductMasterDataStatus!: ActionStatus;

  @productSearchState.State
  readonly getProductDataDistributionStatus!: ActionStatus;

  @productSearchState.State
  readonly getProductOrdersStatus!: ActionStatus;

  @productSearchState.State
  readonly getProductRelatedCalendarsStatus!: ActionStatus;

  @productSearchState.State
  readonly getProductRelatedSPPsStatus!: ActionStatus;

  @productSearchState.State
  readonly getProductRawImportStatus!: ActionStatus;

  @productSearchState.State
  readonly currentProductRawImport!: ProductRawImport | null;

  @productSearchState.State
  readonly currentProductDataDistribution!: ProductDataDistribution | null;

  @productSearchState.State
  readonly currentProductIdentification!: ProductIdentification | null;

  @productSearchState.Action
  readonly getProductIdentification!: UserAction<void>;

  @productSearchState.Action
  readonly getProductMasterData!: UserAction<void>;

  @productSearchState.Action
  readonly getProductDataDistribution!: UserAction<void>;

  @productSearchState.Action
  readonly getProductOrders!: UserAction<void>;

  @productSearchState.Action
  readonly getProductRelatedCalendars!: UserAction<Medium>;

  @productSearchState.Action
  readonly getProductRelatedSPPs!: UserAction<Medium>;

  @productSearchState.Action
  readonly getProductRawImport!: UserAction<void>;

  @productSearchState.Mutation
  readonly setCurrentProductId!: (productId: string) => void;

  readonly languageMap = languageMap;

  currentTab = defaultTab;

  get isLoadingProductIdentification(): boolean {
    return this.getProductIdentificationStatus === ActionStatus.InProgress
      || this.getProductIdentificationsByEANStatus === ActionStatus.InProgress
      || this.currentProductIdentification === null;
  }

  get isLoadingDataDistribution(): boolean {
    return this.isLoadingProductIdentification || this.getProductDataDistributionStatus === ActionStatus.InProgress;
  }

  get isLoadingMasterData(): boolean {
    return this.isLoadingProductIdentification
      || this.getProductMasterDataStatus === ActionStatus.InProgress;
  }

  get isLoadingProductOrders(): boolean {
    return this.isLoadingProductIdentification
      || this.getProductOrdersStatus === ActionStatus.InProgress;
  }

  get isLoadingProductRelatedCalendars(): boolean {
    return this.isLoadingProductIdentification
      || this.getProductRelatedCalendarsStatus === ActionStatus.InProgress;
  }

  get isLoadingProductRelatedSPPs(): boolean {
    return this.isLoadingProductIdentification
      || this.getProductRelatedSPPsStatus === ActionStatus.InProgress;
  }

  get isLoadingRawImport(): boolean {
    return this.isLoadingProductIdentification
      || this.getProductRawImportStatus === ActionStatus.InProgress;
  }

  get isLoadingDetails(): boolean {
    return this.isLoadingMasterData
      || this.isLoadingDataDistribution
      || this.isLoadingProductOrders
      || this.isLoadingProductRelatedCalendars
      || this.isLoadingProductRelatedSPPs
      || this.isLoadingRawImport;
  }

  get jobSummary(): string {
    if (this.isLoadingDataDistribution) {
      return 'Verteilung: Jobs laden...';
    }

    const lines = [];

    if (this.hasDoneJobs) {
      lines.push(`Verteilung: ${this.numberOfDoneJobs} ${this.numberOfDoneJobs === 1 ? 'Job' : 'Jobs'} abgeschlossen`);
    }
    if (this.hasScheduledJobs) {
      lines.push(`Verteilung: ${this.numberOfScheduledJobs} ${this.numberOfScheduledJobs === 1 ? 'Job' : 'Jobs'} geplant`);
    }
    if (this.hasFailedJobs) {
      lines.push(`Verteilung: ${this.numberOfFailedJobs} ${this.numberOfFailedJobs === 1 ? 'Job' : 'Jobs'} fehlgeschlagen`);
    }

    if (lines.length === 0) {
      return 'Verteilung: Keine Jobs vorhanden';
    }

    return lines.join(', ');
  }

  get hasDoneJobs(): boolean {
    return this.numberOfDoneJobs > 0;
  }

  get hasScheduledJobs(): boolean {
    return this.numberOfScheduledJobs > 0;
  }

  get hasFailedJobs(): boolean {
    return this.numberOfFailedJobs > 0;
  }

  get numberOfDoneJobs(): number {
    if (this.isLoadingDataDistribution || !this.currentProductDataDistribution) {
      return 0;
    }

    return this.currentProductDataDistribution!.jobs
      .filter((job) => job.status === 'DONE')
      .length;
  }

  get numberOfScheduledJobs(): number {
    if (this.isLoadingDataDistribution || !this.currentProductDataDistribution) {
      return 0;
    }

    return this.currentProductDataDistribution!.jobs
      .filter((job) => job.status === 'SCHEDULED')
      .length;
  }

  get numberOfFailedJobs(): number {
    if (this.isLoadingDataDistribution || !this.currentProductDataDistribution) {
      return 0;
    }

    return this.currentProductDataDistribution!.jobs
      .filter((job) => job.status !== 'DONE' && job.status !== 'SCHEDULED')
      .length;
  }

  mounted(): void {
    this.triggerProductIdentificationUpdate();
  }

  @Watch('$route.path')
  triggerProductIdentificationUpdate(): void {
    this.setCurrentProductId(this.$route.params.id);
    this.currentTab = defaultTab;

    this.getProductIdentification()
      .then(() => this.updateCurrentProductIdentification())
      .catch((error) => showErrorResponse(error));
  }

  updateCurrentProductIdentification(): void {
    this.getProductMasterData()
      .catch((error) => showErrorResponse(error));

    this.getProductDataDistribution()
      .catch((error) => showErrorResponse(error));

    this.getProductOrders()
      .catch((error) => showErrorResponse(error));

    this.getProductRelatedCalendars(this.currentProductIdentification!.medium)
      .catch((error) => showErrorResponse(error));

    this.getProductRelatedSPPs(this.currentProductIdentification!.medium)
      .catch((error) => showErrorResponse(error));

    this.getProductRawImport()
      .catch((error) => showErrorResponse(error));
  }

  translatedMedium(medium: Medium): string {
    return mediumMap[medium];
  }

}
